/**
 * Os endereços da jornada de SPOT, num lugar só.
 *
 * Mesmo desenho do `WIZARD_PATH`/`LIST_PATH` da locação (ver `Landing/Landing.tsx`):
 * a jornada tem poucos endereços, cada um significa uma coisa, e quem navega
 * para eles está espalhado — o formulário, o cartão do quadro, o acompanhamento,
 * o espelho de vendas, o painel. Escritos à mão em cada um, era só questão de
 * tempo até dois discordarem sobre o mesmo lugar.
 *
 * Só constantes e funções puras: nada de React aqui, para que o `Router` e o
 * portão possam importar sem arrastar a tela da porta para dentro do chunk
 * principal.
 */
import { EnumType, partnersRoutes } from '@/pages/Partners/types/paths';

const PARTNERS = `/${partnersRoutes.partners.main}`;
const FORM_PATH = `${PARTNERS}/${partnersRoutes.indicate.main}/${partnersRoutes.indicate.form.main}`;

/** A porta do SPOT. É para cá que o card único do painel aponta. */
export const SPOT_ENTRY_PATH = `${PARTNERS}/${partnersRoutes.indicate.main}/${EnumType.spot}`;

/** Fazer indicação: nome e telefone, e a Seazone assume daí para a frente. */
export const COLD_PATH = `${FORM_PATH}/${EnumType.coldInvestment}`;

/**
 * A base do formulário de reserva. Sozinha não é etapa nenhuma: normaliza para
 * `NOVA_PATH` (ver `Content.tsx`), e existe para os links antigos continuarem
 * chegando em algum lugar.
 */
export const RESERVE_PATH = `${FORM_PATH}/${EnumType.spot}`;

/**
 * O segmento da indicação que ainda não existe.
 *
 * Mesmo papel do `nova` da locação (`indicar/nova` → `indicar/nova/:id`): a URL
 * diz em que ETAPA o parceiro está, e a etapa 1 precisa de um nome próprio para
 * poder dizer isso. Sem ele a etapa 1 seria a ausência de um segmento, e
 * "ausência" é o que a URL diz quando não sabe.
 *
 * Palavra reservada, então: nenhuma indicação pode ter `nova` como id. É o que
 * `deveRenderizarEtapaDois` verifica antes de tratar o segmento como indicação.
 */
export const NOVA = 'nova';

/**
 * Etapa 1 — a indicação: unidade, dados e documentos do investidor.
 *
 * Nada da seleção viaja na URL. O par empreendimento/unidade que o espelho de
 * vendas entrega vai no `state` da navegação, como sempre foi — é a mesma
 * fronteira da locação, onde as etapas anteriores ao POST também não têm o que
 * recuperar de um F5: o que ainda não foi ao servidor não existe fora da aba.
 */
export const NOVA_PATH = `${RESERVE_PATH}/${NOVA}`;

/** Onde as indicações de SPOT aparecem depois de enviadas. */
export const SPOT_LIST_PATH = `${PARTNERS}/${partnersRoutes.indications.main}/${EnumType.spot}`;

/**
 * Etapa 2 — o fluxo de pagamento daquela indicação.
 *
 * O `nova` é SUBSTITUÍDO pelo id assim que a indicação nasce, como o wizard da
 * locação faz. A partir daí a URL sozinha reconstrói a tela: empreendimento,
 * unidade e o fluxo já enviado saem do `journey/` desta indicação.
 */
export const etapa2Path = (id: number | string) => `${RESERVE_PATH}/${id}`;

/**
 * A etapa 2 de uma indicação com fluxo aprovado, a pedido do parceiro.
 *
 * O parâmetro é o PEDIDO, e só a etapa 2 decide se ele vale (ver `refazer` em
 * `etapa2EDona`): sem ele, quem chega em `FLUXO_APROVADO` vai para o
 * acompanhamento.
 */
export const refazerFluxoPath = (id: number | string) => `${etapa2Path(id)}?refazer=1`;

/** O acompanhamento: onde a indicação vive depois que o fluxo saiu do formulário. */
export const acompanhamentoPath = (id: number | string) => `${SPOT_LIST_PATH}/${id}`;

/** Os dados do contrato, reentrantes: o parceiro volta aqui dias depois. */
export const contratoPath = (id: number | string) => `${acompanhamentoPath(id)}/contrato`;
